import { Checkbox, Chip, type Selection } from '@heroui/react';
import { FiExternalLink, FiMaximize2 } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import { ROUTES } from '@/config/routes';
import { cn, Dialog } from '@/core/ui';
import type { FilteredRecordReview } from '@/graphql/gql/graphql';
import { useGlobal } from '@/store/global';
import { getDbTypeHashFragment } from '@/utils';

const FilteredReviewCard = ({
  row,
  serialNo,
  isSelected,
  onSelect,
}: {
  row: FilteredRecordReview;
  serialNo: number;
  isSelected: boolean;
  onSelect: (id: string, selected: boolean) => void;
}) => {
  const dbType = useGlobal.use.dbType();
  const questionId = row.question?.id ?? row.recordId;
  const editQuestionPath =
    questionId != null
      ? ROUTES.CONTENT.EDIT.QUESTION.replace(':id', String(questionId)) +
        getDbTypeHashFragment('global-state', { dbType })
      : null;

  return (
    <div
      className={cn(
        'bg-content1 rounded-xl border shadow-sm flex flex-col gap-2 px-3 pt-3 pb-1 h-full transition-colors',
        isSelected ? 'border-primary-300 bg-primary-50/30' : 'border-default-200',
      )}
    >
      {/* Header */}
      <div className='flex items-center justify-between gap-2'>
        <div className='flex items-center gap-1.5 min-w-0'>
          <span className='text-xs font-mono text-default-400 shrink-0'>#{serialNo}</span>
          <Chip size='sm' variant='flat' color='default' className='text-xs font-mono shrink-0'>
            {row.recordId ?? '—'}
          </Chip>
        </div>
        <Checkbox
          size='sm'
          isSelected={isSelected}
          onValueChange={(checked) => onSelect(String(row.recordId ?? row.question?.id), checked)}
          aria-label='Select record'
        />
      </div>

      {/* Reason */}
      <div className='rounded-lg p-2 bg-content2 border border-default-200'>
        <code className='text-sm text-default-800 wrap-break-word whitespace-pre-wrap line-clamp-4'>
          {row.reason || '-'}
        </code>
      </div>

      {/* Stats */}
      <div className='flex items-center gap-2 flex-wrap'>
        {row.likes != null && <span className='text-xs text-success-600 font-medium'>👍 {row.likes}</span>}
        {row.dislikes != null && (
          <span className='text-xs text-danger-600 font-medium'>👎 {row.dislikes}</span>
        )}
        <Chip
          size='sm'
          variant='faded'
          radius='sm'
          color='default'
          className='text-xs'
          title={`Total comments${row.newComments ? ` (${row.newComments} new)` : ''}`}
        >
          {row.newComments || 0} / {typeof row.totalComments === 'number' ? row.totalComments : '—'}
        </Chip>
        {row.percentage != null && (
          <Chip size='sm' variant='faded' radius='sm' color='default' className='text-xs'>
            {row.percentage}%
          </Chip>
        )}
      </div>

      {/* Footer */}
      <div className='flex items-center justify-between border-t border-default-100 mt-auto py-1'>
        <Dialog
          title='Reason'
          trigger={(onOpen) => (
            <button
              type='button'
              title='View full reason'
              onClick={onOpen}
              className='p-1 rounded-md hover:bg-primary-100 text-default-600 hover:text-primary-700 transition-colors'
            >
              <FiMaximize2 className='size-3.5' />
            </button>
          )}
        >
          <div className='overflow-y-auto max-h-[60vh]'>
            <code className='text-sm text-default-800 wrap-break-word whitespace-pre-wrap'>{row.reason}</code>
          </div>
        </Dialog>

        {editQuestionPath && (
          <Link
            target='_blank'
            rel='noopener noreferrer'
            to={editQuestionPath}
            className='flex items-center gap-1 text-xs text-default-500 hover:text-primary-500'
          >
            <FiExternalLink className='size-3 shrink-0' />
            <span className='font-mono'>Q: {String(questionId)}</span>
          </Link>
        )}
      </div>
    </div>
  );
};

export const FilteredRecordReviewsGrid = ({
  rows,
  selectedKeys,
  onSelectionChange,
}: {
  rows: Array<{ row: FilteredRecordReview; serialNo: number }>;
  selectedKeys: Selection;
  onSelectionChange: (keys: Selection) => void;
}) => {
  const selectedSet = selectedKeys === 'all' ? null : (selectedKeys as Set<string>);

  const handleSelect = (id: string, selected: boolean) => {
    if (selectedKeys === 'all') return;
    const next = new Set(selectedSet ?? []);
    if (selected) next.add(id);
    else next.delete(id);
    onSelectionChange(next);
  };

  return (
    <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 p-4 overflow-auto max-h-[calc(100vh-460px)] scrollbar-thin'>
      {rows.map(({ row, serialNo }) => {
        const key = String(row.recordId ?? row.question?.id ?? serialNo);
        return (
          <FilteredReviewCard
            key={key}
            row={row}
            serialNo={serialNo}
            isSelected={selectedKeys === 'all' || (selectedSet?.has(key) ?? false)}
            onSelect={handleSelect}
          />
        );
      })}
    </div>
  );
};
